import type { RouteRecordRaw } from 'vue-router'
import type { Tenant } from '@/config/tenant'
import AppConfig from '@/config'

import HomeView from '@/views/HomeView.vue'
import ThreadRoomsView from '@/views/ThreadRoomsView.vue'
import ThreadView from '@/views/ThreadView.vue'
import ProfileView from '@/views/ProfileView.vue'
import SubswaitView from '@/views/SubswaitView.vue'
import NotFoundView from '@/views/NotFoundView.vue'
import ErrorSubscribeView from '@/views/ErrorSubscribeView.vue'

declare module 'vue-router' {
	interface RouteMeta {
		title?: string
		smallPadding?: boolean
		infoPage?: boolean
		auth?: 'internal' | 'entry'
		tenants?: Tenant[]
		locals?: string[]
		requiresSecret?: boolean
		checkDeletedThread?: boolean
		guestOnly?: boolean
		requiresLoginEmail?: boolean
		// Маршрут с auth: 'internal', в который гость всё же допускается (только b2c + internal).
		guestAllowed?: boolean
		// В Метрику уходит путь без query: в нём одноразовый секрет (тикет OAuth).
		hitWithoutQuery?: boolean
	}
}

export const routes: RouteRecordRaw[] = [
	{ path: '/', name: 'Home', component: HomeView, meta: { auth: 'entry' } },
	{
		path: '/thread-rooms',
		name: 'threadRooms',
		component: ThreadRoomsView,
		meta: { auth: 'internal', title: 'История чатов', smallPadding: true }
	},
	{
		path: '/profile',
		component: ProfileView,
		// Инпута на кабинете нет, но .container резервирует под него ~192px снизу и даёт
		// лишний скролл. Снимаем резерв только в b2c: в легаси-вёрстке в этой полосе стоит
		// телепортированная кнопка «Выйти».
		meta: { auth: 'internal', title: 'Личный кабинет', smallPadding: AppConfig.internalB2C }
	},
	{
		path: '/thread/:id',
		name: 'threadId',
		// guestAllowed обязателен: ThreadView делает router.replace(`/thread/${id}`),
		// как только приходит id треда — иначе гостя выкинуло бы посреди стрима.
		component: ThreadView,
		meta: { auth: 'internal', checkDeletedThread: true, title: 'Новый чат', guestAllowed: true }
	},
	{
		path: '/thread',
		name: 'thread',
		component: ThreadView,
		meta: { auth: 'internal', title: 'Новый чат', guestAllowed: true }
	},
	{
		path: '/thread-secret',
		name: 'thread-secret',
		component: ThreadView,
		meta: { requiresSecret: true, title: 'Секретный  чат' } // двойной пробел — сохранить дословно
	},
	{ path: '/subswait', component: SubswaitView, meta: { infoPage: true } },
	{
		path: '/privacy-policy',
		component: () => import('@/views/PrivacyPolicyView.vue'),
		meta: { infoPage: true, title: 'Политика конфиденциальности' }
	},
	{
		path: '/eula',
		component: () => import('@/views/EulaView.vue'),
		meta: { infoPage: true, title: 'Пользовательское соглашение' }
	},
	{
		path: '/cookie-policy',
		// тернарный импорт legal-view — НЕ трогаем (Фаза 1, Шаг 4.5)
		// RU-редакция (p3) живёт по российскому праву, поэтому у неё свой текст политики.
		component:
			AppConfig.tenant === 'tcell'
				? () => import('@/views/CookiePolicyTcellView.vue')
				: AppConfig.local === 'RU'
					? () => import('@/views/CookiePolicyRuView.vue')
					: () => import('@/views/CookiePolicyByView.vue'),
		meta: {
			infoPage: true,
			title: 'Политика обработки файлов cookie',
			// В b2c ссылка на cookie-политику обязательна по тексту согласия на /login,
			// поэтому тенантный гейт снимаем; для остальных редакций поведение прежнее.
			...(AppConfig.internalB2C ? {} : { tenants: ['tcell'] as Tenant[], locals: ['BY'] })
		}
	},
	{
		path: '/terms-of-service',
		component: () => import('@/views/TermsOfServiceView.vue'),
		meta: { infoPage: true, title: 'Правила предоставления услуги', tenants: ['tcell'] }
	},
	// Внутренний вход по e-mail + OTP и онбординг после него — только розничная b2c-редакция
	// с internal-авторизацией (в external вход на внешнем сервисе, в b2b его нет).
	...(AppConfig.internalB2C
		? ([
				{
					path: '/login',
					name: 'Login',
					component: () => import('@/views/LoginView.vue'),
					// meta.auth не ставим: гость обязан попадать на эту страницу
					meta: { title: 'Вход', smallPadding: true, guestOnly: true }
				},
				{
					path: '/login/code',
					name: 'LoginCode',
					component: () => import('@/views/LoginCodeView.vue'),
					meta: { title: 'Вход', smallPadding: true, guestOnly: true, requiresLoginEmail: true }
				},
				{
					// Сюда бэк возвращает после входа через провайдера: ?code=<тикет> или ?error=<код>
					path: '/auth/callback',
					name: 'AuthCallback',
					component: () => import('@/views/AuthCallbackView.vue'),
					meta: { title: 'Вход', smallPadding: true, guestOnly: true, hitWithoutQuery: true }
				},
				{
					path: '/onboarding',
					name: 'Onboarding',
					component: () => import('@/views/OnboardingView.vue'),
					meta: { title: 'Добро пожаловать', smallPadding: true, auth: 'internal' }
				},
				{
					// Отдельный роут, а не локальное состояние ProfileView: работает браузерное
					// «назад», заголовок живёт в meta вместе с остальными, и на экран можно дать
					// прямую ссылку из письма об автосписании.
					path: '/profile/subscription',
					name: 'Subscription',
					component: () => import('@/views/SubscriptionView.vue'),
					meta: { auth: 'internal', title: 'Управление подпиской', smallPadding: true }
				}
			] as RouteRecordRaw[])
		: []),
	...(AppConfig.devSetting
		? [
				{
					path: '/dev-settings999',
					name: 'DevSettings',
					component: () => import('@/views/dev/DevSettings.vue')
				} as RouteRecordRaw
			]
		: []),
	{
		// ловит все несуществующие пути
		path: '/:pathMatch(.*)*',
		name: 'NotFound',
		component: NotFoundView,
		meta: { auth: 'internal', title: 'Страница не найдена', smallPadding: true }
	},
	{
		path: '/error-subscribe',
		name: 'errorSubscribe',
		component: ErrorSubscribeView,
		meta: { auth: 'internal', smallPadding: true }
	}
]
